// SettingsPlanBillingCard.jsx

import React from 'react';

// Components
import Button from 'components/button/Button';

/**
 * SettingsPlanBillingCard
 *
 * Renders the workspace billing summary card and cancel/manage billing actions.
 *
 * @param {object} billingRecord - Current workspace billing record.
 * @param {string} billingSubscriptionId - Active Stripe subscription ID.
 * @param {boolean} canCancelPaidSubscription - Whether the cancel action can run.
 * @param {boolean} canOpenBillingPortal - Whether the billing portal can be opened.
 * @param {string} currentPlanTitle - Active workspace plan title.
 * @param {boolean} isCancelSubscriptionConfirming - Whether cancel confirmation is open.
 * @param {boolean} isCancellingSubscription - Whether subscription cancellation is in flight.
 * @param {boolean} isOpeningBillingPortal - Whether the billing portal is opening.
 * @param {function} onAbortCancelSubscription - Called when cancel confirmation is dismissed.
 * @param {function} onCancelSubscription - Called when cancellation is confirmed.
 * @param {function} onOpenBillingPortal - Called when the billing portal is requested.
 * @param {function} onStartCancelSubscription - Called when cancellation confirmation opens.
 * @param {object} theme - Active theme tokens.
 *
 * @returns {ReactNode} The rendered billing card.
 */
const SettingsPlanBillingCard = React.memo(({
    billingRecord,
    billingSubscriptionId,
    canCancelPaidSubscription,
    canOpenBillingPortal,
    currentPlanTitle,
    isCancelSubscriptionConfirming,
    isCancellingSubscription,
    isOpeningBillingPortal,
    onAbortCancelSubscription,
    onCancelSubscription,
    onOpenBillingPortal,
    onStartCancelSubscription,
    theme,
}) => {
    if (!billingSubscriptionId) {
        return null;
    }

    return (
        <div
            className="settings-modal-plan-summary-card"
            style={{
                borderColor: theme.backgroundHover,
            }}
        >
            <div className="settings-modal-plan-summary-header">
                <div
                    className="settings-modal-plan-summary-title"
                    style={{
                        color: theme.foreground,
                    }}
                >
                    Billing
                </div>
                <div
                    className="settings-modal-plan-summary-price"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    {billingRecord?.planTitle || currentPlanTitle}
                </div>
            </div>

            <div
                className="settings-modal-setting-description"
                style={{
                    color: theme.foregroundMuted,
                }}
            >
                Active Stripe billing stays tied to the workspace until the subscription is canceled.
            </div>

            <div className="settings-modal-plan-facts">
                <div className="settings-modal-plan-fact">
                    <span
                        className="settings-modal-plan-fact-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Billing email
                    </span>
                    <span
                        className="settings-modal-plan-fact-value"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        {billingRecord?.billingEmail || '—'}
                    </span>
                </div>

                <div className="settings-modal-plan-fact">
                    <span
                        className="settings-modal-plan-fact-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Seats billed
                    </span>
                    <span
                        className="settings-modal-plan-fact-value"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        {Number(billingRecord?.seatCount || 0).toLocaleString() || '0'}
                    </span>
                </div>
            </div>

            {canOpenBillingPortal && !isCancelSubscriptionConfirming && (
                <div className="settings-modal-plan-checkout-actions">
                    <Button
                        label={isOpeningBillingPortal ? 'Opening Portal...' : 'Manage Billing'}
                        enabled={!isOpeningBillingPortal}
                        onClick={onOpenBillingPortal}
                    />
                </div>
            )}

            {canCancelPaidSubscription && !isCancelSubscriptionConfirming && (
                <div className="settings-modal-plan-checkout-actions">
                    <Button
                        label="Cancel Subscription"
                        onClick={onStartCancelSubscription}
                    />
                </div>
            )}

            {isCancelSubscriptionConfirming && (
                <>
                    <div
                        className="settings-modal-note"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Cancelling billing downgrades this workspace to the default free plan immediately, if one is available.
                    </div>

                    <div className="settings-modal-plan-checkout-actions">
                        <Button
                            label="Keep Subscription"
                            onClick={onAbortCancelSubscription}
                        />

                        <Button
                            label={isCancellingSubscription ? 'Cancelling...' : 'Confirm Cancel'}
                            enabled={!isCancellingSubscription}
                            onClick={onCancelSubscription}
                        />
                    </div>
                </>
            )}
        </div>
    );
});

SettingsPlanBillingCard.displayName = 'SettingsPlanBillingCard';

export default SettingsPlanBillingCard;